import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Sparkles, UserRoundCog } from 'lucide-react'
import {
  ApiError,
  approveCustomerRequest,
  fetchAiSystemSettings,
  fetchFinanceConsole,
  fetchForecastViews,
  fetchWeeklySummary,
} from '@/api/client'
import type { ConsoleSegment } from '@/features/console/types'
import { ControlRow } from '@/components/ControlRow'
import { DataTable, TableBody, TableHeader } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { ActualsImportPanel } from '@/components/ActualsImportPanel'
import { ExportPublishToolbar } from '@/components/ExportPublishToolbar'
import { HubPage } from '@/components/HubPage'
import { IconButton } from '@/components/IconButton'
import { LoadingState } from '@/components/LoadingState'
import { StatusIcon } from '@/components/StatusIcon'
import { ChatPanel } from '@/features/ai/ChatPanel'
import { formatDate, formatDateTime } from '@/lib/format-date'
import { useNavigate, useSearchParams } from 'react-router-dom'

const SEGMENTS: { id: ConsoleSegment; label: string }[] = [
  { id: 'changes', label: 'Changes' },
  { id: 'hygiene', label: 'Hygiene' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'unconfirmed', label: 'Unconfirmed' },
  { id: 'requests', label: 'Requests' },
]

function segmentFromSearch(raw: string | null): ConsoleSegment {
  const match = SEGMENTS.find((entry) => entry.id === raw)
  return match?.id ?? 'changes'
}

export function FinanceConsolePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [segment, setSegment] = useState<ConsoleSegment>(() =>
    segmentFromSearch(searchParams.get('segment')),
  )
  const [weeklyOpen, setWeeklyOpen] = useState(false)
  const [aiAssistOpen, setAiAssistOpen] = useState(false)
  const [aiActivityBadge, setAiActivityBadge] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const aiAssistButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const fromUrl = segmentFromSearch(searchParams.get('segment'))
    setSegment((current) => (current === fromUrl ? current : fromUrl))
  }, [searchParams])

  const selectSegment = useCallback(
    (next: ConsoleSegment) => {
      setSegment(next)
      setSearchParams(
        (prev) => {
          const copy = new URLSearchParams(prev)
          if (next === 'changes') copy.delete('segment')
          else copy.set('segment', next)
          return copy
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const consoleQuery = useQuery({
    queryKey: ['finance', 'console'],
    queryFn: fetchFinanceConsole,
  })

  const aiSettingsQuery = useQuery({
    queryKey: ['ai', 'settings', 'system'],
    queryFn: fetchAiSystemSettings,
    staleTime: 60_000,
  })
  const aiAssistEnabled = Boolean(
    aiSettingsQuery.data?.ai_enabled && aiSettingsQuery.data?.channels?.chat,
  )

  const aiAssistOpenRef = useRef(false)
  aiAssistOpenRef.current = aiAssistOpen

  function openAiAssist() {
    setAiActivityBadge(false)
    setAiAssistOpen(true)
  }

  function closeAiAssist() {
    setAiAssistOpen(false)
    window.setTimeout(() => aiAssistButtonRef.current?.focus(), 0)
  }

  function toggleAiAssist() {
    if (aiAssistOpenRef.current) closeAiAssist()
    else openAiAssist()
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'j') return
      if (!aiAssistEnabled) return
      event.preventDefault()
      if (aiAssistOpenRef.current) {
        setAiAssistOpen(false)
        window.setTimeout(() => aiAssistButtonRef.current?.focus(), 0)
      } else {
        setAiActivityBadge(false)
        setAiAssistOpen(true)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [aiAssistEnabled])

  const viewsQuery = useQuery({
    queryKey: ['config', 'views'],
    queryFn: fetchForecastViews,
  })

  const weeklyQuery = useQuery({
    queryKey: ['finance', 'weekly-summary'],
    queryFn: fetchWeeklySummary,
  })

  const approveMutation = useMutation({
    mutationFn: approveCustomerRequest,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['finance', 'console'] }),
  })

  const data = consoleQuery.data
  const segmentIndex = SEGMENTS.findIndex((s) => s.id === segment)

  const focusSegment = useCallback(
    (index: number) => {
      const next = (index + SEGMENTS.length) % SEGMENTS.length
      const item = SEGMENTS[next]
      if (!item) return
      selectSegment(item.id)
      window.setTimeout(() => tabRefs.current[next]?.focus(), 0)
    },
    [selectSegment],
  )

  return (
    <>
      <HubPage
        chrome={
          <>
            <ControlRow
              sticky={false}
              leading={
                <>
                  <h1 className="page__title">Finance console</h1>
                  <p className="page__subtitle">
                    {data?.since_last_publish
                      ? `Since last publish · ${data.since_last_publish}`
                      : 'No publish recorded yet'}
                  </p>
                </>
              }
              actions={
                <>
                  {aiAssistEnabled && (
                    <span className="ai-assist-entry">
                      <IconButton
                        ref={aiAssistButtonRef}
                        label={aiAssistOpen ? 'Close AI assist (⌘J)' : 'Open AI assist (⌘J)'}
                        variant="ghost"
                        active={aiAssistOpen}
                        onClick={toggleAiAssist}
                      >
                        <Sparkles size={18} strokeWidth={2} />
                      </IconButton>
                      {!aiAssistOpen && aiActivityBadge ? (
                        <span className="ai-assist-entry__badge" aria-hidden />
                      ) : null}
                    </span>
                  )}
                  <ExportPublishToolbar
                    views={viewsQuery.data ?? []}
                    onPublished={() => void consoleQuery.refetch()}
                  />
                </>
              }
            >
              <div className="segmented" role="tablist" aria-label="Console segments">
                {SEGMENTS.map((item, index) => (
                  <button
                    key={item.id}
                    ref={(el) => {
                      tabRefs.current[index] = el
                    }}
                    type="button"
                    role="tab"
                    id={`console-tab-${item.id}`}
                    aria-selected={segment === item.id}
                    aria-controls={`console-panel-${item.id}`}
                    tabIndex={segment === item.id ? 0 : -1}
                    className={`segmented__item${segment === item.id ? ' segmented__item--active' : ''}`}
                    onClick={() => selectSegment(item.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowRight') {
                        event.preventDefault()
                        focusSegment(segmentIndex + 1)
                      } else if (event.key === 'ArrowLeft') {
                        event.preventDefault()
                        focusSegment(segmentIndex - 1)
                      } else if (event.key === 'Home') {
                        event.preventDefault()
                        focusSegment(0)
                      } else if (event.key === 'End') {
                        event.preventDefault()
                        focusSegment(SEGMENTS.length - 1)
                      }
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </ControlRow>

            <details
              className="accordion"
              open={weeklyOpen}
              onToggle={(event) => setWeeklyOpen((event.target as HTMLDetailsElement).open)}
            >
              <summary className="accordion__summary">
                Weekly summary
                <span className="page__subtitle">Copy for publish commentary</span>
              </summary>
              <div className="accordion__body">
                {weeklyQuery.isLoading && <LoadingState label="Building summary…" />}
                {weeklyQuery.data && (
                  <>
                    <textarea
                      className="field__input field__input--textarea"
                      readOnly
                      rows={4}
                      value={weeklyQuery.data.text}
                      aria-label="Weekly summary text"
                    />
                    <button
                      type="button"
                      className="button button--ghost button--small"
                      onClick={() =>
                        void navigator.clipboard.writeText(weeklyQuery.data?.text ?? '')
                      }
                    >
                      Copy to clipboard
                    </button>
                  </>
                )}
              </div>
            </details>

            <details className="accordion">
              <summary className="accordion__summary">
                Import actual revenue
                <span className="page__subtitle">
                  Download forecast template, edit in Excel, import
                </span>
              </summary>
              <div className="accordion__body">
                <ActualsImportPanel />
              </div>
            </details>
          </>
        }
      >
        {consoleQuery.isLoading && <LoadingState variant="skeleton-table" columns={5} />}
        {consoleQuery.error && (
          <ErrorState
            message={
              consoleQuery.error instanceof ApiError
                ? consoleQuery.error.message
                : 'Console unavailable.'
            }
            onRetry={() => void consoleQuery.refetch()}
          />
        )}

        {data && (
          <div
            className="hub-page__panel"
            role="tabpanel"
            id={`console-panel-${segment}`}
            aria-labelledby={`console-tab-${segment}`}
          >
            {segment === 'changes' && (
              <SegmentTable
                empty={!data.changes?.length}
                emptyTitle="No changes since last publish"
                headers={['When', 'Opportunity', 'Customer', 'Owner', 'Summary', '']}
              >
                {(data.changes ?? []).map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.changed_at)}</td>
                    <td>{row.opportunity_name}</td>
                    <td>{row.customer_name}</td>
                    <td>{row.owner_name}</td>
                    <td>{row.summary}</td>
                    <td>
                      <IconButton
                        label="Open opportunity"
                        variant="ghost"
                        onClick={() => navigate(`/opportunities?selected=${row.opportunity_id}`)}
                      >
                        <ExternalLink size={16} strokeWidth={2} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </SegmentTable>
            )}

            {segment === 'hygiene' && (
              <SegmentTable
                empty={!data.hygiene?.length}
                emptyTitle="No hygiene issues"
                headers={['', 'Opportunity', 'Customer', 'Owner', 'Issue', '']}
              >
                {(data.hygiene ?? []).map((row) => (
                  <tr key={`${row.opportunity_id}-${row.check_code}`}>
                    <td>
                      <StatusIcon status={row.severity} message={row.message} />
                    </td>
                    <td>{row.opportunity_name}</td>
                    <td>{row.customer_name}</td>
                    <td>{row.owner_name}</td>
                    <td>
                      <div className="console-hygiene-issue">
                        <span className="console-hygiene-issue__title">{row.message}</span>
                        {row.guidance?.trim() ? (
                          <span className="console-hygiene-issue__guidance">{row.guidance}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <IconButton
                        label="Open opportunity"
                        variant="ghost"
                        onClick={() => navigate(`/opportunities?selected=${row.opportunity_id}`)}
                      >
                        <ExternalLink size={16} strokeWidth={2} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </SegmentTable>
            )}

            {segment === 'alerts' && (
              <SegmentTable
                empty={!data.alerts?.length}
                emptyTitle="No alert activity"
                headers={['Sent', 'Opportunity', 'Owner', 'Rule', 'Response']}
              >
                {(data.alerts ?? []).map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.sent_at)}</td>
                    <td>{row.opportunity_name}</td>
                    <td>{row.owner_name}</td>
                    <td>{row.rule_name}</td>
                    <td>{row.response ?? '—'}</td>
                  </tr>
                ))}
              </SegmentTable>
            )}

            {segment === 'unconfirmed' && (
              <SegmentTable
                empty={!data.unconfirmed?.length}
                emptyTitle="All owners confirmed"
                headers={['Owner', 'Email', 'Week', 'Opportunities', '']}
              >
                {(data.unconfirmed ?? []).map((row) => (
                  <tr key={row.owner_id}>
                    <td>{row.owner_name}</td>
                    <td>{row.owner_email}</td>
                    <td>{row.week_label}</td>
                    <td>{row.opportunity_count}</td>
                    <td>
                      <IconButton
                        label="Open owner lines to reassign"
                        variant="ghost"
                        onClick={() =>
                          navigate(`/opportunities?search=${encodeURIComponent(row.owner_name)}`)
                        }
                      >
                        <UserRoundCog size={16} strokeWidth={2} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </SegmentTable>
            )}

            {segment === 'requests' && (
              <SegmentTable
                empty={!data.customer_requests?.length}
                emptyTitle="No customer requests"
                headers={['Requested name', 'Requester', 'Notes', 'Created', '']}
              >
                {(data.customer_requests ?? []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.requested_name}</td>
                    <td>{row.requester_name}</td>
                    <td>{row.notes ?? '—'}</td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={approveMutation.isPending}
                        onClick={() => approveMutation.mutate(row.id)}
                      >
                        Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </SegmentTable>
            )}
          </div>
        )}
      </HubPage>

      <ChatPanel
        open={aiAssistOpen}
        onClose={closeAiAssist}
        onOpenOpportunity={(opportunityId) => {
          setAiAssistOpen(false)
          navigate(`/opportunities?selected=${encodeURIComponent(opportunityId)}`)
        }}
        onConfirmed={() => {
          void queryClient.invalidateQueries({ queryKey: ['finance', 'console'] })
          void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
        }}
        onActivityDone={() => setAiActivityBadge(true)}
      />
    </>
  )
}

function SegmentTable({
  headers,
  children,
  empty,
  emptyTitle,
}: {
  headers: string[]
  children: ReactNode
  empty: boolean
  emptyTitle: string
}) {
  if (empty) {
    return <EmptyState title={emptyTitle} />
  }
  return (
    <DataTable dense fill>
      <TableHeader>
        <tr>
          {headers.map((header, index) => (
            <th key={`${header}-${index}`} scope="col">
              {header || <span className="visually-hidden">Actions</span>}
            </th>
          ))}
        </tr>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </DataTable>
  )
}
